import React, { useState, useEffect } from 'react';
import {
  Modal, Form, Input, Select, Alert, Avatar,
  Descriptions, Tag, message, Row, Col, Divider,
  Button
} from 'antd';
import {
  UserOutlined, PhoneOutlined, HomeOutlined,
  IdcardOutlined, CarOutlined, CheckCircleOutlined,
  PlayCircleOutlined, ClockCircleOutlined,
  EnvironmentOutlined, MailOutlined,
  EditOutlined,
  PlusOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../utils/axios';
import ReusableTable from '../components/ReusableTable';

const { Option } = Select;
const { TextArea } = Input;

const ChauffeurPage = () => {
  const [form] = Form.useForm();
  const [chauffeurs, setChauffeurs] = useState([]);
  const [filteredChauffeurs, setFilteredChauffeurs] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [editingChauffeur, setEditingChauffeur] = useState(null);
  const [selectedChauffeur, setSelectedChauffeur] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({
    search: '',
    statut: '',
    operator: ''
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Charger les chauffeurs
  const fetchChauffeurs = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/chauffeurs');
     
      if (response.data.success) {
        let chauffeursArray = [];
       
        if (response.data.data && response.data.data.chauffeurs) {
          chauffeursArray = response.data.data.chauffeurs;
        } else if (Array.isArray(response.data.data)) {
          chauffeursArray = response.data.data;
        } else {
          chauffeursArray = [response.data.data];
        }
       
        const chauffeursData = chauffeursArray.map(chauffeur => ({
          ...chauffeur,
          id: chauffeur._id || chauffeur.id,
          dateEnregistrement: chauffeur.dateEnregistrement ?
            dayjs(chauffeur.dateEnregistrement).format('DD/MM/YYYY HH:mm') :
            dayjs().format('DD/MM/YYYY HH:mm'),
          shortId: chauffeur.shortId || `CH${(chauffeur._id || chauffeur.id).toString().substring(0, 6)}`,
          formattedCreatedAt: chauffeur.createdAt ? formatDate(chauffeur.createdAt) : '',
          formattedUpdatedAt: chauffeur.updatedAt ? formatDate(chauffeur.updatedAt) : ''
        }));
       
        setChauffeurs(chauffeursData);
        setFilteredChauffeurs(chauffeursData);
        message.success(`${chauffeursData.length} chauffeurs chargés`);
      } else {
        message.error(response.data.message || 'Erreur lors du chargement');
      }
    } catch (error) {
      console.error('Erreur chargement chauffeurs:', error);
      message.error('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return dayjs(date).format('DD/MM/YYYY HH:mm');
    } catch {
      return dateString;
    }
  };

  // Appliquer les filtres
  const applyFilters = () => {
    let filtered = [...chauffeurs];
   
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(chauffeur =>
        (chauffeur.nom?.toLowerCase().includes(searchLower)) ||
        (chauffeur.prenom?.toLowerCase().includes(searchLower)) ||
        (chauffeur.telephone?.toLowerCase().includes(searchLower)) ||
        (chauffeur.cni?.toLowerCase().includes(searchLower)) ||
        (chauffeur.numeroMatriculeVehicule?.toLowerCase().includes(searchLower)) ||
        (chauffeur.adresse?.toLowerCase().includes(searchLower))
      );
    }
   
    if (filters.statut) {
      filtered = filtered.filter(chauffeur => chauffeur.statut === filters.statut);
    }
   
    if (filters.operator) {
      filtered = filtered.filter(chauffeur => {
        const cleaned = chauffeur.telephone?.replace(/\D/g, '') || '';
        const prefix = cleaned.startsWith('221') ? cleaned.substring(3, 5) : cleaned.substring(0, 2);
        return prefix === filters.operator;
      });
    }
   
    setFilteredChauffeurs(filtered);
    setCurrentPage(1);
  };

  useEffect(() => {
    fetchChauffeurs();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, chauffeurs]);

  // Gestion des filtres
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Ouvrir modal
  const showModal = (chauffeur = null) => {
    setEditingChauffeur(chauffeur);
    setErrorMessage('');
   
    if (chauffeur) {
      let telephoneValue = chauffeur.telephone || '';
      if (telephoneValue && !telephoneValue.includes('+221') && telephoneValue.length >= 2) {
        const cleaned = telephoneValue.replace(/\D/g, '');
        if (cleaned.startsWith('221')) {
          telephoneValue = `+221 ${cleaned.substring(3)}`;
        } else {
          telephoneValue = `+221 ${cleaned}`;
        }
      }
     
      form.setFieldsValue({
        prenom: chauffeur.prenom || '',
        nom: chauffeur.nom || '',
        telephone: telephoneValue,
        adresse: chauffeur.adresse || '',
        cni: chauffeur.cni || '',
        numeroMatriculeVehicule: chauffeur.numeroMatriculeVehicule || '',
        statut: chauffeur.statut || 'Libre',
        email: chauffeur.email || '',
        ville: chauffeur.ville || '',
        permisNumero: chauffeur.permisNumero || '',
        permisExpiration: chauffeur.permisExpiration || ''
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        telephone: '+221 ',
        statut: 'Libre'
      });
    }
    setIsModalVisible(true);
  };

  // Ouvrir modal de détail
  const showDetailModal = (chauffeur) => {
    setSelectedChauffeur(chauffeur);
    setIsDetailModalVisible(true);
  };

  // Fermer modals
  const handleCancel = () => {
    setIsModalVisible(false);
    setIsDetailModalVisible(false);
    setEditingChauffeur(null);
    setSelectedChauffeur(null);
    setErrorMessage('');
    form.resetFields();
  };

  // Soumettre le formulaire
  const handleSubmit = async (values) => {
    setSubmitting(true);
    setErrorMessage('');
   
    try {
      // Nettoyer le téléphone
      let cleanPhone = values.telephone.replace(/\s/g, '').replace(/[^\d+]/g, '');
      if (cleanPhone.startsWith('+221')) {
        cleanPhone = cleanPhone.replace('+221', '221');
      } else if (!cleanPhone.startsWith('221')) {
        cleanPhone = '221' + cleanPhone;
      }
     
      const formattedData = {
        ...values,
        telephone: cleanPhone,
        numeroMatriculeVehicule: values.numeroMatriculeVehicule.toUpperCase(),
        permisNumero: values.permisNumero?.toUpperCase() || ''
      };

      let response;
      if (editingChauffeur) {
        response = await api.put(`/api/chauffeurs/${editingChauffeur.id || editingChauffeur._id}`, formattedData);
      } else {
        response = await api.post('/api/chauffeurs', formattedData);
      }

      if (response.data.success) {
        message.success(editingChauffeur ? 'Chauffeur modifié avec succès !' : 'Chauffeur ajouté avec succès !');
        fetchChauffeurs();
        handleCancel();
      } else {
        setErrorMessage(response.data.message || 'Erreur lors de l\'opération');
      }
    } catch (error) {
      console.error('Erreur sauvegarde chauffeur:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Erreur lors de la sauvegarde';
      setErrorMessage(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  // Supprimer un chauffeur
  const handleDelete = async (chauffeur) => {
    Modal.confirm({
      title: 'Confirmer la suppression',
      content: `Êtes-vous sûr de vouloir supprimer le chauffeur ${chauffeur.prenom} ${chauffeur.nom} ?`,
      okText: 'Supprimer',
      okType: 'danger',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          const response = await api.delete(`/api/chauffeurs/${chauffeur.id || chauffeur._id}`);
          if (response.data.success) {
            message.success('Chauffeur supprimé avec succès !');
            fetchChauffeurs();
          } else {
            message.error(response.data.message || 'Erreur lors de la suppression');
          }
        } catch (error) {
          console.error('Erreur suppression chauffeur:', error);
          message.error('Erreur lors de la suppression: ' + error.message);
        }
      }
    });
  };

  // Changer le statut
  const handleStatusChange = async (chauffeur) => {
    const newStatut = chauffeur.statut === 'Libre' ? 'En Mission' :
                     chauffeur.statut === 'En Mission' ? 'Inactif' : 'Libre';
   
    try {
      const response = await api.patch(`/api/chauffeurs/${chauffeur.id || chauffeur._id}/status`, {
        statut: newStatut
      });
      if (response.data.success) {
        message.success(`Statut changé à "${newStatut}" avec succès !`);
        fetchChauffeurs();
      } else {
        message.error(response.data.message || 'Erreur lors du changement de statut');
      }
    } catch (error) {
      console.error('Erreur changement statut:', error);
      message.error('Erreur lors du changement de statut');
    }
  };

  // Validation
  const validateCNI = (_, value) => {
    if (!value) return Promise.reject(new Error('Veuillez saisir le numéro CNI'));
    const cniRegex = /^\d{12}$/;
    if (!cniRegex.test(value)) {
      return Promise.reject(new Error('Le numéro CNI doit contenir 12 chiffres'));
    }
    return Promise.resolve();
  };

  const validateTelephone = (_, value) => {
    if (!value) return Promise.reject(new Error('Veuillez saisir le numéro de téléphone'));
   
    const cleaned = value.replace(/\s/g, '').replace(/[^\d+]/g, '');
    const phoneRegex = /^(?:\+221|221)?(77|78|76|70)\d{7}$/;
   
    if (!phoneRegex.test(cleaned)) {
      return Promise.reject(new Error('Format téléphone invalide. Ex: +221 77 123 45 67'));
    }
   
    return Promise.resolve();
  };

  const validateMatricule = (_, value) => {
    if (!value) return Promise.reject(new Error('Veuillez saisir le numéro de matricule'));
   
    const matriculeRegex = /^[A-Z]{2}-\d{4}-[A-Z]{2}$|^[A-Z0-9]{6,10}$/i;
   
    if (!matriculeRegex.test(value)) {
      return Promise.reject(new Error('Format matricule invalide. Ex: DK-1234-AB ou ABC123'));
    }
   
    return Promise.resolve();
  };

  const validateEmail = (_, value) => {
    if (!value) return Promise.resolve(); // Optionnel
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return Promise.reject(new Error('Format email invalide'));
    }
    return Promise.resolve();
  };

  // Formater le téléphone
  const formatPhoneForDisplay = (phone) => {
    if (!phone) return '';
   
    const cleaned = phone.replace(/\D/g, '');
    let formatted = '';
   
    if (cleaned.startsWith('221')) {
      const localPart = cleaned.substring(3);
      if (localPart.length >= 2) {
        formatted = `+221 ${localPart.substring(0, 2)}`;
        if (localPart.length > 2) formatted += ` ${localPart.substring(2, 5)}`;
        if (localPart.length > 5) formatted += ` ${localPart.substring(5, 7)}`;
        if (localPart.length > 7) formatted += ` ${localPart.substring(7, 9)}`;
      }
    } else {
      if (cleaned.length >= 2) {
        formatted = cleaned.substring(0, 2);
        if (cleaned.length > 2) formatted += ` ${cleaned.substring(2, 5)}`;
        if (cleaned.length > 5) formatted += ` ${cleaned.substring(5, 7)}`;
        if (cleaned.length > 7) formatted += ` ${cleaned.substring(7, 9)}`;
      }
    }
   
    return formatted || phone;
  };

  // Options de statut
  const statusOptions = [
    {
      value: 'Libre',
      label: 'Libre',
      color: '#52c41a',
      backgroundColor: '#f6ffed',
      borderColor: '#b7eb8f',
      icon: <CheckCircleOutlined />
    },
    {
      value: 'En Mission',
      label: 'En Mission',
      color: '#fa8c16',
      backgroundColor: '#fff7e6',
      borderColor: '#ffd591',
      icon: <PlayCircleOutlined />
    },
    {
      value: 'Inactif',
      label: 'Inactif',
      color: '#f5222d',
      backgroundColor: '#fff1f0',
      borderColor: '#ffa39e',
      icon: <ClockCircleOutlined />
    }
  ];

  // Calculer les statistiques
  const stats = {
    total: filteredChauffeurs.length,
    libre: filteredChauffeurs.filter(c => c.statut === 'Libre').length,
    'en mission': filteredChauffeurs.filter(c => c.statut === 'En Mission').length,
    inactif: filteredChauffeurs.filter(c => c.statut === 'Inactif').length
  };

  // Données paginées
  const paginatedData = filteredChauffeurs.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Configuration des colonnes
  const columns = [
    {
      title: 'Prénom',
      dataIndex: 'prenom',
      key: 'prenom',
      width: 120,
      render: (text) => <span style={{ fontWeight: '500' }}>{text}</span>,
      cellStyle: { fontWeight: '500' }
    },
    {
      title: 'Nom',
      dataIndex: 'nom',
      key: 'nom',
      width: 120,
      render: (text) => <span style={{ fontWeight: '600' }}>{text}</span>,
      cellStyle: { fontWeight: '600' }
    },
    {
      title: 'Téléphone',
      dataIndex: 'telephone',
      key: 'telephone',
      width: 150,
      render: (telephone) => {
        const formatted = formatPhoneForDisplay(telephone);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <PhoneOutlined style={{ color: '#1890ff', fontSize: '14px' }} />
            <span style={{
              fontFamily: 'monospace',
              fontSize: '13px',
              fontWeight: '600',
              color: '#1890ff'
            }}>
              {formatted || telephone}
            </span>
          </div>
        );
      }
    },
    {
      title: 'CNI',
      dataIndex: 'cni',
      key: 'cni',
      width: 140,
      render: (cni) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <IdcardOutlined style={{ color: '#722ed1', fontSize: '14px' }} />
          <span style={{
            fontFamily: 'monospace',
            fontSize: '13px',
            fontWeight: '500',
            backgroundColor: '#f9f0ff',
            padding: '4px 8px',
            borderRadius: '4px'
          }}>
            {cni}
          </span>
        </div>
      )
    },
    {
      title: 'Matricule',
      dataIndex: 'numeroMatriculeVehicule',
      key: 'matricule',
      width: 140,
      render: (matricule) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <CarOutlined style={{ color: '#52c41a', fontSize: '14px' }} />
          <span style={{
            fontFamily: 'monospace',
            fontSize: '13px',
            fontWeight: '700',
            backgroundColor: '#f6ffed',
            padding: '4px 8px',
            borderRadius: '4px',
            border: '1px solid #b7eb8f'
          }}>
            {matricule}
          </span>
        </div>
      )
    },
    {
      title: 'Date enreg.',
      dataIndex: 'dateEnregistrement',
      key: 'dateEnregistrement',
      width: 150,
      render: (date) => (
        <div style={{
          fontSize: '12px',
          color: '#666',
          fontFamily: 'monospace'
        }}>
          {date}
        </div>
      )
    }
  ];

  // Configuration des filtres
  const tableFilters = [
    {
      field: 'statut',
      placeholder: 'Statut',
      value: filters.statut,
      options: statusOptions.map(opt => ({
        value: opt.value,
        label: (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {opt.icon}
            <span>{opt.label}</span>
          </div>
        )
      })),
      width: '140px'
    },
    {
      field: 'operator',
      placeholder: 'Opérateur',
      value: filters.operator,
      options: [
        { value: '77', label: '🟠 Orange (77)' },
        { value: '78', label: '🟢 Free (78)' },
        { value: '70', label: '🟣 Expresso (70)' },
        { value: '76', label: '🔵 Promobile (76)' }
      ],
      width: '160px'
    }
  ];

  return (
    <>
      <ReusableTable
        // Configuration de base
        title="Gestion des Chauffeurs"
        data={paginatedData}
        columns={columns}
        loading={loading}
       
        // Pagination
        totalItems={filteredChauffeurs.length}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={(page, size) => {
          setCurrentPage(page);
          if (size) setPageSize(size);
        }}
        onSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
       
        // Recherche et filtres
        searchText={filters.search}
        onSearchChange={(value) => handleFilterChange('search', value)}
        onSearch={applyFilters}
        filters={tableFilters}
        onFilterChange={handleFilterChange}
       
        // Actions
        onAdd={() => showModal()}
        onRefresh={fetchChauffeurs}
        onEdit={showModal}
        onDelete={handleDelete}
        onView={showDetailModal}
        onStatusChange={handleStatusChange}
       
        // Personnalisation
        addButtonText="Nouveau Chauffeur"
        searchPlaceholder="Rechercher chauffeur..."
        showStats={true}
        stats={stats}
       
        // Colonnes spéciales
        showStatusColumn={true}
        statusOptions={statusOptions}
       
        // Configuration des boutons d'action
        actionButtons={['view', 'edit', 'status', 'delete']}
       
        // Personnalisation des couleurs
        primaryColor="#5fa5e7"
        borderColor="#5fa5e7"
       
        // Options supplémentaires
        extraButtons={
          <Button
            type="default"
            onClick={() => {
              setFilters({ search: '', statut: '', operator: '' });
              message.info('Filtres réinitialisés');
            }}
            style={{
              borderColor: '#ff4d4f',
              color: '#ff4d4f'
            }}
          >
            Réinitialiser
          </Button>
        }
       
        // Contenu personnalisé
        headerContent={
          <div style={{ marginBottom: '20px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
              paddingBottom: '16px',
              borderBottom: '1px solid #e8e8e8'
            }}>
              <div>
                <h1 style={{
                  margin: '0 0 8px 0',
                  fontSize: '24px',
                  fontWeight: '600',
                  color: '#333',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <UserOutlined style={{ color: '#5fa5e7' }} />
                  Gestion des Chauffeurs
                </h1>
                <div style={{
                  fontSize: '14px',
                  color: '#666',
                  display: 'flex',
                  gap: '24px',
                  alignItems: 'center'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    <span><strong>{stats.libre}</strong> Libre{stats.libre > 1 ? 's' : ''}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <PlayCircleOutlined style={{ color: '#fa8c16' }} />
                    <span><strong>{stats['en mission']}</strong> En Mission</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <ClockCircleOutlined style={{ color: '#f5222d' }} />
                    <span><strong>{stats.inactif}</strong> Inactif{stats.inactif > 1 ? 's' : ''}</span>
                  </div>
                  <div style={{
                    backgroundColor: '#5fa5e7',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontWeight: '500'
                  }}>
                    Total: {stats.total}
                  </div>
                </div>
              </div>
             
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => showModal()}
                size="large"
                style={{
                  backgroundColor: '#5fa5e7',
                  borderColor: '#5fa5e7',
                  padding: '0 24px',
                  height: '40px',
                  fontWeight: '500',
                  borderRadius: '6px',
                  boxShadow: '0 2px 8px rgba(95, 165, 231, 0.3)'
                }}
              >
                Nouveau Chauffeur
              </Button>
            </div>
          </div>
        }
      />

      {/* Modal pour ajouter/modifier un chauffeur */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {editingChauffeur ? (
              <>
                <EditOutlined style={{ color: '#5fa5e7', fontSize: '20px' }} />
                <span style={{ fontSize: '18px', fontWeight: '600' }}>Modifier le Chauffeur</span>
              </>
            ) : (
              <>
                <PlusOutlined style={{ color: '#5fa5e7', fontSize: '20px' }} />
                <span style={{ fontSize: '18px', fontWeight: '600' }}>Nouveau Chauffeur</span>
              </>
            )}
          </div>
        }
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={700}
        style={{ top: 20 }}
        bodyStyle={{ padding: 0 }}
        destroyOnClose
      >
        {errorMessage && (
          <Alert
            message="Erreur"
            description={errorMessage}
            type="error"
            showIcon
            style={{
              margin: '20px 20px 0 20px',
              borderRadius: '6px'
            }}
            closable
            onClose={() => setErrorMessage('')}
          />
        )}
     
        <div style={{ padding: '24px' }}>
          <div style={{
            marginBottom: '24px',
            paddingBottom: '16px',
            borderBottom: '1px solid #e8e8e8'
          }}>
            <h2 style={{
              margin: '0',
              fontSize: '16px',
              fontWeight: '600',
              color: '#333'
            }}>
              Informations personnelles
            </h2>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            style={{ marginTop: '8px' }}
          >
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="prenom"
                  label="Prénom"
                  rules={[
                    { required: true, message: 'Veuillez saisir le prénom' },
                    { min: 2, message: 'Minimum 2 caractères' },
                    { max: 50, message: 'Maximum 50 caractères' }
                  ]}
                >
                  <Input
                    placeholder="Ex: Moussa"
                    prefix={<UserOutlined style={{ color: '#999' }} />}
                    size="large"
                    allowClear
                    style={{ borderRadius: '6px' }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="nom"
                  label="Nom"
                  rules={[
                    { required: true, message: 'Veuillez saisir le nom' },
                    { min: 2, message: 'Minimum 2 caractères' },
                    { max: 50, message: 'Maximum 50 caractères' }
                  ]}
                >
                  <Input
                    placeholder="Ex: Diallo"
                    prefix={<UserOutlined style={{ color: '#999' }} />}
                    size="large"
                    allowClear
                    style={{ borderRadius: '6px' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="telephone"
                  label="Téléphone"
                  rules={[{ validator: validateTelephone }]}
                  help={
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      <PhoneOutlined /> Format: +221 77 123 45 67
                    </div>
                  }
                >
                  <Input
                    placeholder="+221 77 123 45 67"
                    prefix={<PhoneOutlined style={{ color: '#999' }} />}
                    size="large"
                    allowClear
                    style={{ borderRadius: '6px' }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="email"
                  label="Email (optionnel)"
                  rules={[{ validator: validateEmail }]}
                >
                  <Input
                    placeholder="exemple@email.com"
                    prefix={<MailOutlined style={{ color: '#999' }} />}
                    size="large"
                    allowClear
                    style={{ borderRadius: '6px' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="cni"
                  label="Numéro CNI"
                  rules={[{ validator: validateCNI }]}
                  help="12 chiffres exactement"
                >
                  <Input
                    placeholder="123456789012"
                    prefix={<IdcardOutlined style={{ color: '#999' }} />}
                    maxLength={12}
                    size="large"
                    allowClear
                    style={{ borderRadius: '6px' }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="statut"
                  label="Statut"
                  rules={[{ required: true, message: 'Veuillez sélectionner le statut' }]}
                >
                  <Select
                    size="large"
                    style={{ borderRadius: '6px' }}
                  >
                    {statusOptions.map(option => (
                      <Option key={option.value} value={option.value}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {option.icon}
                          <span>{option.label}</span>
                        </div>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <div style={{
              margin: '24px 0',
              paddingBottom: '16px',
              borderBottom: '1px solid #e8e8e8'
            }}>
              <h2 style={{
                margin: '0',
                fontSize: '16px',
                fontWeight: '600',
                color: '#333'
              }}>
                Informations véhicule
              </h2>
            </div>

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="numeroMatriculeVehicule"
                  label="N° Matricule Véhicule"
                  rules={[{ validator: validateMatricule }]}
                  help="Ex: DK-1234-AB ou ABC123"
                >
                  <Input
                    placeholder="DK-1234-AB"
                    prefix={<CarOutlined style={{ color: '#999' }} />}
                    size="large"
                    allowClear
                    style={{ borderRadius: '6px' }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="permisNumero"
                  label="N° Permis (optionnel)"
                >
                  <Input
                    placeholder="Ex: PERM123456"
                    size="large"
                    allowClear
                    style={{ borderRadius: '6px' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="ville"
                  label="Ville"
                >
                  <Input
                    placeholder="Ex: Dakar"
                    prefix={<EnvironmentOutlined style={{ color: '#999' }} />}
                    size="large"
                    allowClear
                    style={{ borderRadius: '6px' }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="permisExpiration"
                  label="Expiration permis (optionnel)"
                >
                  <Input
                    type="date"
                    size="large"
                    style={{ borderRadius: '6px' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <div style={{
              margin: '24px 0',
              paddingBottom: '16px',
              borderBottom: '1px solid #e8e8e8'
            }}>
              <h2 style={{
                margin: '0',
                fontSize: '16px',
                fontWeight: '600',
                color: '#333'
              }}>
                Adresse
              </h2>
            </div>

            <Form.Item
              name="adresse"
              label="Adresse complète"
              rules={[
                { required: true, message: 'Veuillez saisir l\'adresse' },
                { min: 5, message: 'Minimum 5 caractères' },
                { max: 200, message: 'Maximum 200 caractères' }
              ]}
            >
              <TextArea
                placeholder="Ex: Dakar, Plateau, Rue 10 x 12, Bâtiment A"
                rows={3}
                showCount
                maxLength={200}
                style={{ borderRadius: '6px' }}
              />
            </Form.Item>

            <Divider style={{ margin: '24px 0' }} />

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              marginTop: '32px',
              paddingTop: '20px',
              borderTop: '1px solid #e8e8e8'
            }}>
              <Button
                onClick={handleCancel}
                disabled={submitting}
                size="large"
                style={{
                  padding: '8px 32px',
                  fontWeight: '500',
                  borderRadius: '6px'
                }}
              >
                Annuler
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                disabled={submitting}
                size="large"
                style={{
                  backgroundColor: '#5fa5e7',
                  borderColor: '#5fa5e7',
                  padding: '8px 32px',
                  fontWeight: '500',
                  borderRadius: '6px'
                }}
              >
                {editingChauffeur ? 'Modifier' : 'Enregistrer'}
              </Button>
            </div>
          </Form>
        </div>
      </Modal>

      {/* Modal de détail */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <UserOutlined style={{ color: '#5fa5e7', fontSize: '20px' }} />
            <span style={{ fontSize: '18px', fontWeight: '600' }}>Détails du Chauffeur</span>
          </div>
        }
        open={isDetailModalVisible}
        onCancel={handleCancel}
        footer={[
          <Button key="close" onClick={handleCancel} size="large" style={{ padding: '8px 24px' }}>
            Fermer
          </Button>,
          selectedChauffeur && (
            <Button
              key="edit"
              type="primary"
              onClick={() => {
                handleCancel();
                showModal(selectedChauffeur);
              }}
              size="large"
              style={{
                backgroundColor: '#5fa5e7',
                borderColor: '#5fa5e7',
                padding: '8px 24px'
              }}
              icon={<EditOutlined  />}
            >
              Modifier
            </Button>
          )
        ]}
        width={600}
      >
        {selectedChauffeur && (
          <div style={{ padding: '20px 0' }}>
            {/* Header avec avatar */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '20px',
              padding: '20px',
              backgroundColor: '#f0f7ff',
              borderRadius: '8px',
              marginBottom: '24px'
            }}>
              <Avatar
                size={80}
                icon={<UserOutlined />}
                style={{
                  backgroundColor: '#5fa5e7',
                  fontSize: '32px'
                }}
              />
              <div style={{ flex: 1 }}>
                <h2 style={{
                  margin: '0 0 8px 0',
                  fontSize: '24px',
                  fontWeight: '600',
                  color: '#333'
                }}>
                  {selectedChauffeur.prenom} {selectedChauffeur.nom}
                </h2>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  {(() => {
                    const statutOption = statusOptions.find(s => s.value === selectedChauffeur.statut);
                    return (
                      <Tag
                        color={statutOption?.color}
                        style={{
                          padding: '6px 16px',
                          fontSize: '14px',
                          fontWeight: '500',
                          borderRadius: '20px'
                        }}
                        icon={statutOption?.icon}
                      >
                        {selectedChauffeur.statut}
                      </Tag>
                    );
                  })()}
                  <Tag
                    color="blue"
                    style={{
                      padding: '6px 16px',
                      fontSize: '14px',
                      fontWeight: '500',
                      borderRadius: '20px'
                    }}
                  >
                    ID: {selectedChauffeur.shortId}
                  </Tag>
                </div>
              </div>
            </div>

            {/* Informations détaillées */}
            <Descriptions
              column={1}
              bordered
              size="middle"
              labelStyle={{
                fontWeight: '600',
                width: '200px',
                backgroundColor: '#fafafa',
                padding: '12px 16px'
              }}
              contentStyle={{
                padding: '12px 16px',
                backgroundColor: 'white'
              }}
              style={{
                borderRadius: '8px',
                overflow: 'hidden'
              }}
            >
              <Descriptions.Item label={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <PhoneOutlined />
                  Téléphone
                </div>
              }>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    backgroundColor: '#e6f7ff',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    fontFamily: 'monospace',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1890ff'
                  }}>
                    {formatPhoneForDisplay(selectedChauffeur.telephone)}
                  </div>
                  {selectedChauffeur.email && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <MailOutlined style={{ color: '#666' }} />
                      <span>{selectedChauffeur.email}</span>
                    </div>
                  )}
                </div>
              </Descriptions.Item>
             
              <Descriptions.Item label={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <IdcardOutlined />
                  CNI
                </div>
              }>
                <div style={{
                  backgroundColor: '#f9f0ff',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  fontFamily: 'monospace',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#722ed1'
                }}>
                  {selectedChauffeur.cni}
                </div>
              </Descriptions.Item>
             
              <Descriptions.Item label={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CarOutlined />
                  Matricule Véhicule
                </div>
              }>
                <div style={{
                  backgroundColor: '#f6ffed',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  fontFamily: 'monospace',
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#52c41a',
                  border: '2px solid #b7eb8f'
                }}>
                  {selectedChauffeur.numeroMatriculeVehicule}
                </div>
              </Descriptions.Item>
             
              <Descriptions.Item label={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <HomeOutlined />
                  Adresse
                </div>
              }>
                <div style={{
                  backgroundColor: '#fff7e6',
                  padding: '12px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  lineHeight: '1.6'
                }}>
                  {selectedChauffeur.adresse}
                </div>
              </Descriptions.Item>
             
              <Descriptions.Item label="Date d'enregistrement">
                <div style={{
                  padding: '8px 12px',
                  backgroundColor: '#fafafa',
                  borderRadius: '6px',
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  {selectedChauffeur.dateEnregistrement}
                </div>
              </Descriptions.Item>
             
              {selectedChauffeur.permisNumero && (
                <Descriptions.Item label="Numéro de permis">
                  <div style={{
                    backgroundColor: '#fff0f6',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#eb2f96'
                  }}>
                    {selectedChauffeur.permisNumero}
                  </div>
                </Descriptions.Item>
              )}
             
              {selectedChauffeur.ville && (
                <Descriptions.Item label="Ville">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <EnvironmentOutlined style={{ color: '#fa8c16' }} />
                    <span style={{ fontSize: '14px', fontWeight: '500' }}>
                      {selectedChauffeur.ville}
                    </span>
                  </div>
                </Descriptions.Item>
              )}
            </Descriptions>
          </div>
        )}
      </Modal>
    </>
  );
};

export default ChauffeurPage;